import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { paymentAllocationMessages } from 'src/features/paymentAllocations/utils/messages';
import { PaymentAllocation } from 'src/features/paymentAllocations/utils/types';
import { useDeletePaymentAllocation } from 'src/features/paymentAllocations/api/useDeletePaymentAllocation';
import { LIST_PAYMENT_ALLOCATIONS_RESOURCE } from 'src/features/paymentAllocations/api/useListPaymentAllocations';

interface DeletePaymentAllocationFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPaymentAllocation: PaymentAllocation;
    children?: React.ReactNode;
}

function DeletePaymentAllocationFormModal<TData>({
    selectedPaymentAllocation,
    children,
}: DeletePaymentAllocationFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { mutate: deletePaymentAllocation } = useDeletePaymentAllocation({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PAYMENT_ALLOCATIONS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(paymentAllocationMessages.deletePaymentAllocationSuccess),
                description: formatMessage(paymentAllocationMessages.deletePaymentAllocationSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...paymentAllocationMessages.deletePaymentAllocation },
                subTitle: {
                    ...paymentAllocationMessages.deletePaymentAllocationSubtitle,
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...paymentAllocationMessages.confirmDeletePaymentAllocation },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: paymentAllocationMessages.deletePaymentAllocation,
                callbackFn: () => {
                    return (
                        deletePaymentAllocation({
                            allocationId: selectedPaymentAllocation.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeletePaymentAllocationFormModal;
